import { logInteraction } from "../../services/api/apiClient";
import { UserAssignmentsMode } from "../../services/api/types/general/homeAssignment";
export {};

declare global {
  interface Window {
    API?: ScormApi12;
    API_1484_11?: ScormApi2004_4;
  }
}

let userId: string;
let assignmentId: string;
let portalId: string;
let interactionSession: string;
let completionPercentage: string;
let username: string;
let stopRedirect: boolean = false;
let pendingRedirectTimer: ReturnType<typeof setTimeout> | null = null;
let isResizing: boolean = false;
let resizeTimer: ReturnType<typeof setTimeout> | null = null;
let assignmentMode: UserAssignmentsMode = UserAssignmentsMode.ENROLLED;
let returnPath: string = "/";
let onWarningMessage: ((message: string) => void) | null = null;

const redirectToHome = () => {
  if (typeof window !== "undefined") {
    window.location.href = returnPath;
  }
};

// Track resize events - set isResizing flag during resize and for 300ms after
const handleResize = () => {
  isResizing = true;
  if (resizeTimer !== null) {
    clearTimeout(resizeTimer);
  }
  resizeTimer = setTimeout(() => {
    isResizing = false;
    resizeTimer = null;
  }, 300);
};

// Set up resize listener
if (typeof window !== "undefined") {
  window.addEventListener("resize", handleResize);
}

// Cancel any pending redirect (called when Initialize happens, meaning iframe reloaded)
const cancelPendingRedirect = () => {
  if (pendingRedirectTimer !== null) {
    clearTimeout(pendingRedirectTimer);
    pendingRedirectTimer = null;
    console.log("Redirect cancelled - SCORM re-initialized");
  }
};

const isCompletionFinished = () => {
  const pct = Number.parseFloat(completionPercentage ?? "");
  return Number.isFinite(pct) && pct >= 100;
};

// Schedule redirect after a delay, but skip if we're in a resize event
const scheduleRedirectAfterTerminate = () => {
  if (stopRedirect) {
    console.log("Redirect skipped - stopRedirect flag is set");
    return;
  }

  // Skip redirect if we're currently resizing
  if (isResizing) {
    console.log("Redirect skipped - resize in progress");
    return;
  }

  // If completion is 100%, redirect immediately (course finished)
  if (isCompletionFinished()) {
    console.log("Redirecting immediately - course completed (100%)");
    redirectToHome();
    return;
  }

  // Debounce: wait 500ms to confirm this isn't a spurious Terminate
  console.log("Scheduling redirect in 500ms...");
  pendingRedirectTimer = setTimeout(() => {
    pendingRedirectTimer = null;
    // Double-check we're not in a resize when the timer fires
    if (isResizing) {
      console.log("Redirect cancelled - resize detected");
      return;
    }
    console.log("Redirecting - user exited course");
    redirectToHome();
  }, 500);
};

export const setUserId = (id: string) => {
  userId = id;
};

export const setUsername = (name: string) => {
  username = name;
};

export const setPortalId = (id: string) => {
  portalId = id;
};

export const setAssignmentId = (id: string) => {
  assignmentId = id;
};

export const setInteractionSession = (session: string) => {
  interactionSession = session;
};

export const setCompletionPercentage = (percentage: string) => {
  completionPercentage = percentage;
};

export const setStopRedirect = (flag: boolean) => {
  stopRedirect = flag;
};

export const setAssignmentMode = (mode: UserAssignmentsMode) => {
  assignmentMode = mode;
};

export const setReturnPath = (path: string) => {
  returnPath = path;
};

export const setOnWarningMessage = (cb: ((message: string) => void) | null) => {
  onWarningMessage = cb;
};

export const resetScormData = () => {
  scormApi.data = {};
  scormApi_1484_11.data = {};
};

// Terminate SCORM session without redirecting (for navigation)
export const terminateWithoutRedirect = async () => {
  if (scormApi.initialized) {
    scormApi.initialized = false;
    if (
      portalId &&
      userId &&
      assignmentId &&
      assignmentMode === UserAssignmentsMode.ENROLLED
    ) {
      console.log("TerminateWithoutRedirect-SCORM 1.2");
      await logInteraction(portalId, userId, assignmentId, {
        interactionSession: interactionSession,
        interactionData: JSON.stringify(scormApi.data),
        completionPercentage: completionPercentage,
        isTerminate: true,
      });
    }
    scormApi.data = {};
  }

  if (scormApi_1484_11.initialized) {
    scormApi_1484_11.initialized = false;
    if (
      portalId &&
      userId &&
      assignmentId &&
      assignmentMode === UserAssignmentsMode.ENROLLED
    ) {
      console.log("TerminateWithoutRedirect-SCORM 2004");
      await logInteraction(portalId, userId, assignmentId, {
        interactionSession: interactionSession,
        interactionData: JSON.stringify(scormApi_1484_11.data),
        completionPercentage: completionPercentage,
        isTerminate: true,
      });
    }
    scormApi_1484_11.data = {};
  }
};

type ScormApi = {
  initialized: boolean;
  data: Record<string, string>;
};

type ScormApi12 = ScormApi & {
  LMSInitialize: () => string;
  LMSFinish: () => string;
  LMSGetValue: (key: string) => string;
  LMSSetValue: (key: string, value: string) => string;
  LMSCommit: () => string;
  LMSGetLastError: () => string;
  LMSGetErrorString: () => string;
  LMSGetDiagnostic: () => string;
};

type ScormApi2004_4 = ScormApi & {
  Initialize: () => string;
  Terminate: () => string;
  GetValue: (key: string) => string;
  SetValue: (key: string, value: string) => string;
  Commit: () => string;
  GetLastError: () => string;
  GetErrorString: () => string;
  GetDiagnostic: () => string;
};

const scormApi: ScormApi12 = {
  initialized: false,
  data: {},

  LMSInitialize: () => {
    cancelPendingRedirect();
    scormApi.initialized = true;
    scormApi.LMSSetValue("cmi.core.student_id", assignmentId);
    scormApi.LMSSetValue("cmi.core.student_name", username);
    return "true";
  },

  LMSFinish: () => {
    scormApi.initialized = false;
    console.log("LMSFinish called");
    scheduleRedirectAfterTerminate();
    stopRedirect = false;
    return "true";
  },

  LMSGetValue: (key: string) => {
    const retVal = scormApi.data[key] || "";
    return retVal;
  },

  LMSSetValue: (key: string, value: string) => {
    scormApi.data[key] = value;
    return "true";
  },

  LMSCommit: () => {
    if (
      portalId &&
      userId &&
      assignmentId &&
      assignmentMode === UserAssignmentsMode.ENROLLED
    ) {
      logInteraction(portalId, userId, assignmentId, {
        interactionSession: interactionSession,
        interactionData: JSON.stringify(scormApi.data),
        completionPercentage: completionPercentage,
        isTerminate: false,
      }).then((res) => {
        if (res?.message && onWarningMessage) {
          onWarningMessage(res.message);
        }
      }).catch(() => {});
      console.log('LMSCommit',scormApi.data);
    }
    return "true";
  },

  LMSGetLastError: () => {
    return "0";
  },

  LMSGetErrorString: () => {
    return "No error";
  },

  LMSGetDiagnostic: () => {
    return "No diagnostic";
  },
};

const scormApi_1484_11: ScormApi2004_4 = {
  initialized: false,
  data: {},

  Initialize: () => {
    cancelPendingRedirect();
    scormApi_1484_11.initialized = true;
    scormApi_1484_11.SetValue("cmi.learner_id", assignmentId);
    scormApi_1484_11.SetValue("cmi.learner_name", username);
    return "true";
  },

  Terminate: () => {
    scormApi_1484_11.initialized = false;
    console.log("Terminate called");
    scheduleRedirectAfterTerminate();
    stopRedirect = false;
    return "true";
  },

  GetValue: (key: string) => {
    const retVal = scormApi_1484_11.data[key] || "";
    return retVal;
  },

  SetValue: (key: string, value: string) => {
    scormApi_1484_11.data[key] = value;
    return "true";
  },

  Commit: () => {
    if (
      portalId &&
      userId &&
      assignmentId &&
      assignmentMode === UserAssignmentsMode.ENROLLED
    ) {
      logInteraction(portalId, userId, assignmentId, {
        interactionSession: interactionSession,
        interactionData: JSON.stringify(scormApi_1484_11.data),
        completionPercentage: completionPercentage,
        isTerminate: false,
      }).then((res) => {
        if (res?.message && onWarningMessage) {
          onWarningMessage(res.message);
        }
      }).catch(() => {});
      console.log('Commit',scormApi_1484_11.data);
    }
    return "true";
  },

  GetLastError: () => {
    return "0";
  },

  GetErrorString: () => {
    return "No error";
  },

  GetDiagnostic: () => {
    return "No diagnostic";
  },
};

window.API = scormApi; // For SCORM 1.2
window.API_1484_11 = scormApi_1484_11; // For SCORM 2004
